import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

import warranty1 from './images/warranty/warranty-1.jpeg';
import warranty2 from './images/warranty/warranty-2.jpeg';
import warranty3 from './images/warranty/warranty-3.jpeg';

const bydWarranty = [
  {
    label: 'Warranty Traction Battery (Baterai)',
    value: '8 Tahun / 160.000 km',
  },
  {
    label: 'Warranty Drive Unit (Motor / Komponen Penggerak)',
    value: '8 Tahun / 150.000 km',
  },
  { label: 'Warranty Vehicle', value: '6 Tahun / 150.000 km' },
  { label: 'Free Maintenance Service', value: '4 Tahun atau 60.000 km' },
];

const denzaWarranty = [
  {
    label: 'Warranty Traction Battery (Baterai)',
    value: '8 Tahun / 160.000 km',
  },
  {
    label: 'Warranty Drive Unit (Motor / Komponen Penggerak)',
    value: '8 Tahun / 150.000 km',
  },
  { label: 'Warranty Vehicle', value: '6 Tahun / 150.000 km' },
  {
    label: 'Free Periodical Maintenance',
    value: '6 Tahun / No Limitation Mileage',
  },
];

function WarrantyCard({ title, subtitle, items, accent }) {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        border: '1px solid #e8e8e8',
      }}
    >
      <Box
        sx={{
          background: accent,
          color: '#fff',
          px: 3,
          py: 2.5,
        }}
      >
        <Typography variant='h5' fontWeight={800}>
          {title}
        </Typography>
        <Typography variant='body2' sx={{ opacity: 0.9, mt: 0.5 }}>
          {subtitle}
        </Typography>
      </Box>

      <CardContent sx={{ p: 0 }}>
        {items.map((item, index) => (
          <Box key={index}>
            <Box
              sx={{
                px: 3,
                py: 2.25,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 1.2,
              }}
            >
              <Typography
                variant='body1'
                sx={{
                  color: '#444',
                  fontWeight: 500,
                }}
              >
                {item.label}
              </Typography>

              <Typography
                variant='body1'
                sx={{
                  color: '#111',
                  fontWeight: 800,
                  textAlign: { xs: 'left', sm: 'right' },
                }}
              >
                {item.value}
              </Typography>
            </Box>
            {index !== items.length - 1 && <Divider />}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

export default function Warranty() {
  return (
    <Box sx={{ bgcolor: '#f7f8fa' }}>
      <Helmet>
        <title>Warranty BYD & Denza | Garansi Resmi BYD BSD Serpong</title>
        <meta
          name='description'
          content='Informasi garansi resmi BYD dan Denza di Indonesia, meliputi garansi baterai, drive unit, vehicle warranty, dan layanan free maintenance.'
        />
      </Helmet>

      {/* HERO */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
          color: '#fff',
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 8, sm: 10, md: 12 },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <Typography
            variant='h1'
            sx={{
              fontSize: { xs: '2rem', sm: '2.8rem', md: '3.4rem' },
              fontWeight: 900,
              letterSpacing: 0.3,
              mb: 2,
            }}
          >
            Warranty BYD & Denza
          </Typography>

          <Typography
            variant='h2'
            sx={{
              fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
              fontWeight: 400,
              color: 'rgba(255,255,255,0.82)',
              maxWidth: 820,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Detail garansi resmi dan layanan purna jual untuk kendaraan BYD dan
            Denza di Indonesia.
          </Typography>
        </Box>
      </Box>

      {/* IMAGE SECTION */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, mt: { xs: 4, sm: 6, md: 8 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Grid container spacing={3}>
            {[warranty1, warranty2, warranty3].map((image, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.14)',
                    bgcolor: '#fff',
                    height: '100%',
                  }}
                >
                  <Box
                    component='img'
                    src={image}
                    alt={`Warranty ${index + 1}`}
                    loading='lazy'
                    sx={{
                      width: '100%',
                      height: { xs: 260, sm: 320, md: 360 },
                      objectFit: 'contain',
                      display: 'block',
                      backgroundColor: '#fff',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 5, sm: 6, md: 8 },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              variant='h3'
              sx={{
                fontSize: { xs: '1.6rem', sm: '2rem', md: '2.3rem' },
                fontWeight: 800,
                color: '#111',
                mb: 1.5,
              }}
            >
              Detail Garansi & Layanan Purna Jual
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#5f6368',
                maxWidth: 760,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Setiap kendaraan mendapatkan perlindungan garansi resmi untuk
              baterai, drive unit, kendaraan, dan layanan servis berkala sesuai
              ketentuan masing-masing brand.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <WarrantyCard
                title='Warranty BYD'
                subtitle='Garansi resmi untuk kendaraan BYD di Indonesia'
                items={bydWarranty}
                accent='linear-gradient(135deg, #111827 0%, #2563eb 100%)'
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <WarrantyCard
                title='Warranty Denza'
                subtitle='Garansi resmi untuk kendaraan Denza di Indonesia'
                items={denzaWarranty}
                accent='linear-gradient(135deg, #111827 0%, #374151 100%)'
              />
            </Grid>
          </Grid>

          {/* NOTE */}
          <Box
            sx={{
              mt: 5,
              p: { xs: 2.5, sm: 3 },
              borderRadius: 4,
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
            }}
          >
            <Stack spacing={1}>
              <Typography variant='h6' fontWeight={800} sx={{ color: '#111' }}>
                Catatan
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: '#5f6368', lineHeight: 1.8 }}
              >
                Informasi warranty dapat berubah mengikuti kebijakan resmi ATPM
                / dealer. Untuk konfirmasi detail unit, periode servis, dan
                syarat klaim garansi, silakan hubungi marketing BYD BSD Serpong.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
